import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    borders, colors, fontSizes, fontWeights, lineHeights, space, radii, mediaQueries
} from 'style/config';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import Link from 'components/Link';
import localeUtils from 'utils/LanguageLocale';
import Empty from 'constants/empty';
import BundleWidgetEN from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_en_US';
import BundleWidgetFR from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(BundleWidgetEN, BundleWidgetFR);

const BundleProductCard = ({ product, isCurrentProduct, isChecked, onCheckboxChange }) => {
    const {
        brandName, variationType, allowSKUChange, productName, displayName, skuImages, listPrice, salePrice, variationValue
    } =
        product || Empty.Object;
    const imageUrl = skuImages?.imageUrl || skuImages?.['image250'];
    const cardProductName = isCurrentProduct ? displayName : productName;
    const showChangeLink = !isCurrentProduct && allowSKUChange && variationType;
    const displayPrice = salePrice || listPrice;

    const renderChangeLink = css =>
        showChangeLink && (
            <Link css={css}>
                {getText('change')} {variationType}
            </Link>
        );

    return (
        <div css={styles.card}>
            <Checkbox
                checked={isCurrentProduct ? true : Boolean(isChecked)}
                onChange={isCurrentProduct ? () => {} : onCheckboxChange}
                aria-label={cardProductName}
                aria-disabled={isCurrentProduct || undefined}
                paddingY={0}
            />
            <div css={styles.contentWrapper}>
                {imageUrl && (
                    <img
                        src={imageUrl}
                        alt={cardProductName}
                        css={styles.image}
                    />
                )}
                <div css={styles.info}>
                    {brandName && <span css={styles.brand}>{brandName}</span>}
                    <span css={styles.productName}>{cardProductName}</span>
                    {variationValue && <span css={styles.variationMobile}>{variationValue}</span>}
                    {isCurrentProduct && <span css={styles.badge}>{getText('currentProduct')}</span>}
                    {renderChangeLink(styles.changeLinkDesktop)}
                </div>
                <div css={styles.priceColumn}>
                    {variationValue && <span css={styles.variationDesktop}>{variationValue}</span>}
                    {displayPrice && <span css={styles.price}>{displayPrice}</span>}
                    {renderChangeLink(styles.changeLinkMobile)}
                </div>
            </div>
        </div>
    );
};

const styles = {
    card: {
        display: 'flex',
        gap: 0,
        alignItems: 'center',
        padding: `${space[3]}px ${space[4]}px`,
        borderBottom: `${borders[1]} ${colors.lightGray}`,
        width: '100%',
        [mediaQueries.sm]: {
            gap: space[1],
            padding: 0,
            borderBottom: 'none'
        }
    },
    contentWrapper: {
        display: 'contents',
        [mediaQueries.sm]: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            gap: space[2]
        }
    },
    image: {
        width: 60,
        height: 60,
        objectFit: 'cover',
        [mediaQueries.sm]: {
            width: 108,
            height: 108
        }
    },
    info: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flex: 1,
        [mediaQueries.sm]: {
            flex: 'none'
        }
    },
    priceColumn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        alignSelf: 'stretch',
        justifyContent: 'space-between',
        [mediaQueries.sm]: {
            alignItems: 'flex-start',
            gap: space[2]
        }
    },
    brand: {
        fontSize: fontSizes.xs,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        [mediaQueries.sm]: {
            fontSize: fontSizes.sm
        }
    },
    productName: {
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight,
        whiteSpace: 'wrap',
        [mediaQueries.sm]: {
            fontSize: fontSizes.base
        }
    },
    variationMobile: {
        fontSize: fontSizes.xs,
        lineHeight: lineHeights.tight,
        color: colors.gray,
        [mediaQueries.sm]: { display: 'none' }
    },
    variationDesktop: {
        display: 'none',
        [mediaQueries.sm]: {
            display: 'block',
            fontSize: fontSizes.xs,
            lineHeight: lineHeights.tight,
            color: colors.gray
        }
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        height: 18,
        backgroundColor: colors.nearWhite,
        borderRadius: radii[2],
        padding: `0 ${space[2]}px`,
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight,
        color: colors.gray,
        marginTop: space[2],
        [mediaQueries.sm]: {
            marginTop: 0
        }
    },
    price: {
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        textAlign: 'right'
    },
    changeLinkMobile: {
        fontSize: fontSizes.sm,
        color: colors.link,
        textAlign: 'right',
        [mediaQueries.sm]: { display: 'none' }
    },
    changeLinkDesktop: {
        display: 'none',
        [mediaQueries.sm]: {
            display: 'block',
            color: colors.link
        }
    }
};

export default wrapFunctionalComponent(BundleProductCard, 'BundleProductCard');
